import React, {FC, useState} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Loader} from '../../../../../libs/shared/ui/src/components/loaders/loader';
import inboxNoContactLinkedToAccount from '../../../../assets/images/inboxNoContactLinkedToAccount.png';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {AccountMetadata, AccountModel, ApiAccountMetadata} from '../../../../models/api/accountModel';
import {fetchApiAsync} from '../../../../services/http/http';
import {fetchAccountByAliasAsyncAction} from '../../../../store/data/accounts/accountsActions';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {popoutAccountDetailsAction} from '../../../../store/panels/accounts/accountDetailsPanelActions';
import {SearchNoResultsHeader} from './searchNoResultsHeader';
import {
  SearchNoResultsMessage,
  SearchNoResultsMessageProps,
  StyledNoResultsFooterDiv,
  StyledNoResultsFooterP,
  StyledNoResultsWrapperDiv,
} from './searchNoResultsMessage';

/*
 * Props.
 */

interface SearchNoResultsAccountProps extends SearchNoResultsMessageProps {
  accountAlias: string;
}

/*
 * Component.
 */

export const SearchNoResultsAccount: FC<SearchNoResultsAccountProps> = (props) => {
  const {panelId, accountAlias, parameters, cellsContext, navigateToSearch} = props;

  const dispatch = useAppDispatch();

  const {isLoading, account, accountMetadata} = useFetchAccountAndMetadata(accountAlias);

  if (isLoading) {
    return <Loader />;
  }

  // If we did not get metadata or if there are associated cards, render the default message.
  if (!accountMetadata || accountMetadata.numCards > 0) {
    return (
      <SearchNoResultsMessage
        panelId={panelId}
        parameters={parameters}
        cellsContext={cellsContext}
        navigateToSearch={navigateToSearch}
      />
    );
  }

  const openAccountPopout = () => account && dispatch(popoutAccountDetailsAction(panelId, account.id));

  return (
    <StyledNoResultsWrapperDiv>
      <SearchNoResultsHeader image={inboxNoContactLinkedToAccount}>
        <Trans id="search-no-results-account-header">No contacts associated with this account</Trans>
      </SearchNoResultsHeader>
      <StyledNoResultsFooterDiv>
        <StyledNoResultsFooterP>
          <Trans id="search-no-results-account-footer">
            Add contacts or email domains to see associated conversations.
          </Trans>
        </StyledNoResultsFooterP>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={openAccountPopout}>
          <Trans id="search-no-results-account-cta">Add contacts</Trans>
        </Button>
      </StyledNoResultsFooterDiv>
    </StyledNoResultsWrapperDiv>
  );
};

/*
 * Helpers.
 */

function useFetchAccountAndMetadata(accountAlias: string) {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [account, setAccount] = useState<AccountModel>();
  const [accountMetadata, setAccountMetadata] = useState<AccountMetadata>();

  const dispatch = useAppDispatch();

  const company = useAppSelector(getCompany);

  const fetchAccountAndMetadata = async () => {
    try {
      const accountResponse = await dispatch(fetchAccountByAliasAsyncAction(accountAlias));
      setAccount(accountResponse);

      const metadata = await fetchApiAsync(
        ApiAccountMetadata,
        `${company.url}/accounts/${accountResponse.id}/account_metadata`,
      );

      setIsLoading(false);
      setAccountMetadata(metadata);
    } catch (err) {
      setIsLoading(false);
    }
  };

  useInitialEffect(() => {
    fetchAccountAndMetadata();
  });

  return {
    isLoading,
    account,
    accountMetadata,
  };
}
